/**
 * 案件番号（プロジェクト番号）生成ユーティリティ
 *
 * 付番ルール: S-YYYYMMDD-XX
 * - S: 固定の識別記号
 * - YYYYMMDD: 案件登録日
 * - XX: A〜Zの英字2文字（0=AA, 1=AB, ..., 675=ZZ）
 */

/** A〜Zのランダムな英字2文字を生成（テスト・後方互換用） */
function randomTwoLetters(): string {
  const A = 65
  const Z = 90
  const c1 = String.fromCharCode(A + Math.floor(Math.random() * (Z - A + 1)))
  const c2 = String.fromCharCode(A + Math.floor(Math.random() * (Z - A + 1)))
  return c1 + c2
}

/** 0〜675 を AA〜ZZ に変換（26*26=676 通り） */
export function indexToTwoLetters(index: number): string {
  const i = Math.max(0, Math.min(675, Math.floor(index)))
  const a = 65
  const high = Math.floor(i / 26)
  const low = i % 26
  return String.fromCharCode(a + high) + String.fromCharCode(a + low)
}

/**
 * 連番をサフィックスに変換（AA〜ZZ → AAA〜ZZZ → AAAA〜...）
 * - 0〜675: 2文字（AA〜ZZ）
 * - 676〜: 3文字（AAA〜ZZZ）、その後4文字（AAAA〜）へ桁上がり
 */
export function indexToSuffix(index: number): string {
  const i = Math.max(0, Math.floor(index))
  if (i < 676) return indexToTwoLetters(i)
  let n = i - 676
  let numDigits = 3
  let maxForDigits = 26 ** 3 // 17576
  while (n >= maxForDigits) {
    n -= maxForDigits
    numDigits++
    maxForDigits *= 26
  }
  const digits: number[] = []
  for (let j = 0; j < numDigits; j++) {
    digits.unshift(n % 26)
    n = Math.floor(n / 26)
  }
  return digits.map((d) => String.fromCharCode(65 + d)).join('')
}

/**
 * 案件番号を生成（S-YYYYMMDD-XX 形式、インデックス指定で一意）
 * プロジェクト作成時は日付ごとのシード last_index と組み合わせて使用する。
 * XX は AA〜ZZ、ZZ の次は AAA〜 と桁が増える。
 * @param registeredAt - 案件登録日（作成日）
 * @param index - その日の連番（0 始まり、全体で一意）
 * @returns 例: S-20250415-CF, S-20250415-AAA
 */
export function generateProjectNumberWithIndex(registeredAt: Date, index: number): string {
  const yyyy = registeredAt.getFullYear()
  const mm = String(registeredAt.getMonth() + 1).padStart(2, '0')
  const dd = String(registeredAt.getDate()).padStart(2, '0')
  const dateStr = `${yyyy}${mm}${dd}`
  return `S-${dateStr}-${indexToSuffix(index)}`
}

/**
 * 案件番号を生成（S-YYYYMMDD-XX 形式、ランダム2文字）
 * @deprecated 被りを防ぐため createProject では generateProjectNumberWithIndex + シードを使用すること
 */
export function generateProjectNumber(registeredAt: Date): string {
  const yyyy = registeredAt.getFullYear()
  const mm = String(registeredAt.getMonth() + 1).padStart(2, '0')
  const dd = String(registeredAt.getDate()).padStart(2, '0')
  const dateStr = `${yyyy}${mm}${dd}`
  return `S-${dateStr}-${randomTwoLetters()}`
}
